import AudioToggleIcon from "../../lib/audio_toggle_icon";
import {
  normalizeErrorEvent,
  normalizePromiseRejectionEvent,
  shouldIgnoreError,
  // if process.env.NODE_ENV === "test"
  isTestError,
  // #endif
} from "../../error_handling";
import * as CreativeUtils from "../../creative_utils";
import * as ClickHandling from "../../click_handling";
import ContentFrame from "./content_frame";
import {
  AUDIO_POLICY_ON_INTERACTION,
  AUDIO_POLICY_NO_RESTRICTIONS,
  CLICK_REJECTION_REASON_NO_INTERACTIONS,
  CLICK_OPEN_METHOD_MRAID,
} from "../../constants";
import EnvData from "./env_data";
import {
  callMraidMethod,
  onMraidStateChangeEvent,
  getMraidProperties,
  initOuterMraid,
} from "./outer_mraid";
import LiftoffMRAID from "../liftoff_mraid";
import Logger from "../logger";
import { getAdexpContextSnapshotFn } from "../adexp";
import { loadLogger } from "./common";
import { appendImpressionTags } from "../../frontendlib/dom_utils";
import Autoclick from "../autoclick/Autoclick";
import addDeadzones, { addDeadzoneStyles } from "../addDeadzones";
import LastChanceEndscreen from "../../lib/last_chance_endscreen/last_chance_endscreen";

// #if process.env.NODE_ENV === "test"
// const testResetFunctions = [];
// const resetForTests = () => {
//   testResetFunctions.forEach((fn) => fn());
//   testResetFunctions.splice(0, testResetFunctions.length);
// };
// #endif

const createGlobalAudioToggle = (contentFrame) => {
  const el = document.createElement("div");
  el.classList.add("liftoff-global-audio-toggle");
  Object.assign(el.style, {
    opacity: "0.5",
    display: "none",
    "pointer-events": "all",
    right: "0",
    bottom: "0",
    position: "absolute",
  });
  const icon = new AudioToggleIcon();
  const iconEl = icon.getElement();
  el.appendChild(iconEl);

  let isCurrentlyMuted = false;
  const setMuted = (shouldMute) => {
    if (shouldMute === isCurrentlyMuted) {
      return;
    }
    isCurrentlyMuted = shouldMute;
    contentFrame.setMuted(shouldMute);
    icon.setMuted(shouldMute);
  };

  let firstInteractionOccurred = false;

  contentFrame.onInteractionChange(() => {
    if (firstInteractionOccurred) {
      return;
    }

    firstInteractionOccurred = true;
    setMuted(false);
  });

  iconEl.addEventListener("click", () => {
    setMuted(!isCurrentlyMuted);
  });

  contentFrame.onMadeSound(() => {
    el.style.display = "inline-block";
  });

  setMuted(true);
  return el;
};

const applyDocumentElStyles = (docEl) => {
  Object.assign(docEl.style, {
    width: "100%",
    height: "100%",
  });
  Object.assign(docEl.querySelector("body").style, {
    width: "100%",
    height: "100%",
    margin: 0,
    overflow: "hidden",
  });
};

const initContainerEl = (containerEl, platform) => {
  // NOTE(Austin): In my testing, only the body style mattered. But the html
  // style shouldn't hurt. We might want to also investigate only setting the
  // width and height to the creative's size.
  applyDocumentElStyles(document.documentElement);

  Object.assign(containerEl.style, {
    position: "absolute",
    // NOTE(Casey): In testing, we've observed exchange elements overlaying our
    // content and preventing clickthroughs. Setting a z-index can prevent this
    // in some cases, though it's not clear we want to put our content over all
    // exchange elements in every case.
    // TODO(Casey): Reconsider this after
    // testing.
    zIndex: "1",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    fontSize: "14px",
    lineHeight: "1.5",
    fontFamily:
      platform === "ios"
        ? "Helvetica Neue,Helvetica,Arial,sans-serif"
        : "Roboto,Helvetica,Arial,sans-serif",
  });
};

const makeScriptEl = ({ srcURL, content }) => {
  if (srcURL != null && content != null) {
    console.warn(
      "Creating a script element with a src and content will result" +
        " in only src being set!"
    );
  }
  const scriptEl = document.createElement("script");
  scriptEl.type = "application/javascript";
  if (srcURL != null) {
    scriptEl.src = srcURL;
  } else if (content != null) {
    scriptEl.innerText = content;
  }

  return scriptEl;
};

const makeBaseEl = (baseURL) => {
  if (baseURL == null || baseURL.length === 0) {
    return null;
  }

  const baseEl = document.createElement("base");
  baseEl.href = baseURL;
  return baseEl;
};

const prependToDocumentHeader = (documentEl, elsToPrepend) => {
  elsToPrepend.reverse().forEach((el) => {
    if (el == null) {
      return;
    }
    documentEl.insertBefore(el, documentEl.children[0]);
  });
};

const htmlStringToDocumentElement = (htmlString) => {
  const doc = document.implementation.createHTMLDocument("");
  // NOTE(Casey): We previously tried inserting the third-party HTML into the
  // document using document.write, but this failed on some exchanges in
  // testing. We've also tried inserting it by setting innerHTML of the document
  // body, but this removes body attributes set in third-party HTML that are
  // required by some creatives. The current approach of setting innerHTML of
  // the html element will prevent html element attributes specified in the
  // third-party HTML from being copied over, so we may need to find a
  // work-around in the future if this breaks any creatives.
  doc.querySelector("html").innerHTML = htmlString;
  return doc.documentElement;
};

const processHTML = ({ rawHTML, rootURL, scriptSrcURLs, envData, inProd }) => {
  let processedHTML = rawHTML.replace(
    /\$\{CLICK_URL\}|\$\{CLICKTHROUGH_URL\}/g,
    envData.getClickthroughURL()
  );

  // Replace the liftoff_api.js v1 script with the liftoff.js v2 script in the
  // DB.
  // TODO(Casey): Remove this when we're fully transitioned to v2.
  const cdnUrl = envData.getCDNUrl();

  const liftoffJSv2Location = inProd
    ? `${cdnUrl}/api/v2/adcreative/liftoff.js?v=2`
    : `${window.location.origin}/dev/madlib_ad_markup/main/v2/liftoff.js`;
  processedHTML = processedHTML.replace(
    "https://cdn.liftoff.io/api/v1/adcreative/liftoff_api.js",
    liftoffJSv2Location
  );

  // NOTE(Austin): This is done to make sure any leftover hard-coded URLs are
  // replaced with china-friendly URLs (so we can spend in China).
  processedHTML = processedHTML.replace(
    /http[s]*:\/\/cdn\.liftoff\.io/,
    cdnUrl
  );

  const docEl = htmlStringToDocumentElement(processedHTML);
  applyDocumentElStyles(docEl);

  // NOTE(Casey): Developers may include <script src="mraid.js"></script> in
  // their markup. Since we provide mraid in the iframe rather than loading it
  // from an external source we remove this tag here. If we find that some
  // developers load this script asynchronously, we may in the future provide an
  // empty "mraid.js" in the creative's root folder at ingestion-time.
  docEl.querySelectorAll("script[src='mraid.js']").forEach((mraidScriptEl) => {
    mraidScriptEl.remove();
  });

  const liftoffJSScriptNodes = docEl.querySelectorAll(
    `script[src='${liftoffJSv2Location}']`
  );
  if (liftoffJSScriptNodes.length === 0) {
    // Add the liftoff.js v2 source if they didn't add it, just in case they
    // need it (and forgot to include it).
    prependToDocumentHeader(docEl, [
      makeScriptEl({
        srcURL: liftoffJSv2Location,
      }),
    ]);
  }

  const encodedJSON = encodeURIComponent(envData.asJSON());
  prependToDocumentHeader(docEl, [
    // If rootURL is null or "", makeBaseEl will return null. Null values aren't
    // prepended.
    makeBaseEl(rootURL),
    ...scriptSrcURLs.map((srcURL) => makeScriptEl({ srcURL })),
    makeScriptEl({
      content: `LiftoffInnerEnv.init(JSON.parse(decodeURIComponent("${encodedJSON}")), "${rootURL}");`,
    }),
    // For Playrix
    makeScriptEl({
      content: [
        `window.addEventListener("load", function () {`,
        `  window.clickInstall = typeof Liftoff !== "undefined" ? Liftoff.open : window.open;`,
        `});`,
      ].join(" "),
    }),
  ]);

  return docEl.outerHTML;
};

const applyLiftoffPolicy = (envData, containerEl, contentFrame) => {
  CreativeUtils.applyLiftoffPolicy(
    containerEl,
    {
      addBorder: envData.getAddBorder(),
      removeAdWrapper: envData.getRemoveAdWrapper(),
      showAdChoices: envData.getShowAdChoices(),
      russianAgeRating: envData.getRussianAgeRating(),
      showChineseWatermark: envData.getShowChineseWatermark(),
      useCustomClose: envData.getUseCustomClose(),
      closeDelay: envData.getCloseDelay(),
      // NOTE(Casey): We allow the outer/inner envs to coordinate control of the
      // audio policy, so we set it to NO_RESTRICTIONS here to ensure
      // CreativeUtils is not applying audio policy control.
      // TODO(Casey):
      // Deprecate this functionality in CreativeUtils.applyLiftoffPolicy when
      // fully transitioned to v2.
      audioPolicy: AUDIO_POLICY_NO_RESTRICTIONS,
    },
    {
      creativeID: envData.getCreativeID(),
      platform: envData.getDevicePlatform(),
      exchange: envData.getExchange(),
      sourceAppName: envData.getSourceAppName(),
      sourceAppID: envData.getSourceAppID(),
      cdnURL: envData.getCDNUrl(),
    }
  );

  // TODO(Casey): Remove handling of audio policy in CreativeUtils when CSP v1
  // is fully deprecated.
  const audioPolicy = envData.getAudioPolicy();
  if (audioPolicy === AUDIO_POLICY_ON_INTERACTION) {
    const globalAudioToggleEl = createGlobalAudioToggle(contentFrame);
    containerEl.appendChild(globalAudioToggleEl);
  }
};

const loadExternalHTML = (url) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener("load", () => {
      resolve(xhr.responseText);
    });
    xhr.addEventListener("readystatechange", () => {
      const STATE_DONE = 4;
      if (xhr.readyState === STATE_DONE && xhr.status >= 400) {
        reject(new Error(`Got ${xhr.status} when loading ${url}.`));
      }
    });
    xhr.addEventListener("error", () => {
      reject(new Error(`Failed to GET ${url}`));
    });
    xhr.open("GET", url);
    xhr.send();
  });
};

const getCompleteDOMSnapshot = (contentFrame) => {
  return contentFrame.getDOMSnapshot().then((innerEnvHTML) => {
    const outerEnvHTML = CreativeUtils.getDOMSnapshot();
    const completeSnapshot = [
      "<!-- Start outer environment HTML -->",
      outerEnvHTML,
      "<!-- End outer environment HTML>",
      "<!-- Start inner environment HTML -->",
      innerEnvHTML,
      "<!-- End inner environment HTML -->",
    ];
    return completeSnapshot.join("\n");
  });
};

const configureHTMLReturn = (envData, contentFrame) => {
  if (!envData.getEnableHTMLReturn()) {
    return;
  }

  const htmlReturnDelay = envData.getHTMLReturnDelay();

  // NOTE(Austin): Since we start this before we've checked that the content
  // frame has loaded, the dom snapshot may or may not return a loaded content
  // frame. This is done intentionally to guarantee we get html returns even
  // when things fail.
  setTimeout(() => {
    getCompleteDOMSnapshot(contentFrame).then((completeDOMSnapshot) => {
      CreativeUtils.postHTMLReturn(
        envData.getLoggingEndpoint(),
        completeDOMSnapshot
      );
    });
  }, htmlReturnDelay || 0);
};

const processNormalizedError = (errorObj) => {
  try {
    const errorStr = errorObj.message || errorObj.toString();
    if (shouldIgnoreError(errorObj)) {
      console.warn(`Ignoring error: ${errorStr}`);
      return;
    }

    Logger.recordError(errorObj);
  } catch (processingError) {
    // Avoid infinite loops.
    console.error(`Error in processError: ${processingError.message}`);
  }
};

const configureErrorLogging = (envData, contentFrame) => {
  const processErrorEvent = (event) => {
    if (event == null) {
      return;
    }

    // #if process.env.NODE_ENV === "test"
//     if (isTestError(event.reason)) {
//       return;
//     }
    // #endif

    const normalizedError = normalizeErrorEvent(event);
    if (normalizedError != null) {
      processNormalizedError(normalizedError);
    }
  };

  const processRejectionEvent = (event) => {
    if (event == null) {
      return;
    }

    // #if process.env.NODE_ENV === "test"
//     if (isTestError(event.reason)) {
//       return;
//     }
    // #endif

    const normalizedError = normalizePromiseRejectionEvent(event);
    if (normalizedError != null) {
      processNormalizedError(normalizedError);
    }
  };

  window.addEventListener("error", processErrorEvent);
  window.addEventListener("unhandledrejection", processRejectionEvent);
  contentFrame.onError(processNormalizedError);

  // #if process.env.NODE_ENV === "test"
//   testResetFunctions.push(() => {
//     window.removeEventListener("error", processErrorEvent);
//     window.removeEventListener("unhandledrejection", processRejectionEvent);
//   });
  // #endif
};

const configureLogging = (envData, contentFrame) => {
  if (!envData.getEnableLogging()) {
    console.warn("Logging is not enabled.");
    return;
  }
  configureErrorLogging(envData, contentFrame);
  configureHTMLReturn(envData, contentFrame);
};

/**
 * NOTE(Casey): Celtra creatives determine their own destination URL, so we need
 * to open that rather than use our click configuration. Celtra notifies our
 * click endpoint (provided in a macro replaced in admarkup.core) server-side.
 * We use mraid/window.open directly to avoid discrepancies on Celtra's side due
 * to possible dysfunction of the click waterfall. (Celtra's creatives use
 * mraid.)
 */
const isCeltraClickthrough = (interceptedDestinationURL) => {
  try {
    const destURL = new URL(interceptedDestinationURL);
    return destURL.host.includes("celtra");
  } catch (_) {
    return false;
  }
};

const handleCreativeManagedClickthrough = (interceptedDestinationURL) => {
  if (window.mraid != null && window.mraid.open != null) {
    window.mraid.open(interceptedDestinationURL);
  } else {
    window.open(interceptedDestinationURL);
  }
};

// On iOS, touch events do not work inside iframes unless the outer window has
// at least one touch event listener. See
// https://stackoverflow.com/a/50483933/7926897.
const enableIframeTouchEvents = () => {
  const handler = () => null;
  window.addEventListener("touchstart", handler);
  // #if process.env.NODE_ENV === "test"
//   testResetFunctions.push(() => {
//     window.removeEventListener("touchstart", handler);
//   });
  // #endif
};

// We're running some Bing, Revcontent & Taboola creatives for 3BlueMedia that
// handle click notifications and that redirect via a bing.com, revcontent.com
// or taboola.com URL.
// On the other hand we have customer DiDi Rider who wants to use DoubleClick
// Campaign Manager's impression tag for rendering and click tracking, and
// customer FanDuel wants to use FlashingTalk tag for same purpose. Their CSP
// creatives do the same as 3BlueMedia ones do by pinging our click tracking and
// mraid.open() the 3rd party tracking URLs to be redirected to dest app page.
// Bannerflow was added due to draftkings 3rd party dynamic ad creatives.
const isCreativeManagedClickthrough = (destURL) => {
  const allowedPrefixRegExp = new RegExp(
    "^https?://([^/]+\\.)*((bannerflow|bing|revcontent|taboola|flashtalking)\\.com|(doubleclick|bannerflow)\\.net)$",
    "i"
  );
  try {
    const validatedUrl = new URL(destURL);
    return allowedPrefixRegExp.test(validatedUrl.origin);
  } catch (ex) {
    return false;
  }
};

const validateContentFrame = (contentFrame) => {
  const errors = [];
  return new Promise((resolve) => {
    contentFrame.containsIframe().then((hasIframe) => {
      if (hasIframe) {
        errors.push("Contains iframe");
      }
      resolve({ errors });
    });
  });
};

let validationResultPromise;
const validationListeners = [];
const vxInteractionListeners = [];
class LiftoffOuterEnv {
  // ScriptSrcURLs should be [polyfills, innerEnvScript]
  static init({
    containerEl,
    validationCheck = false,
    envData: rawEnvData,
    scriptSrcURLs = [],
    rootURL,
    html,
    externalHtmlURL,
    inProd,
    onViewableTrackingTags = [],
    useAutoendcardBehaviour,
  }) {
    if (onViewableTrackingTags != null) {
      LiftoffMRAID.onViewable(() => {
        const trackingTagHolder = document.getElementById(
          "tracking-tag-holder"
        );
        appendImpressionTags(trackingTagHolder, onViewableTrackingTags);
      });
    }

    const envData = new EnvData(rawEnvData);
    if (
      envData.getIsInterstitial() &&
      // TODO(oscar): Remove after crserve-deadzone test is over.
      envData.hasABTestGroup("crserve-deadzone-v2-experiment")
    ) {
      addDeadzoneStyles();
      addDeadzones(true, containerEl);
    }

    initOuterMraid();
    enableIframeTouchEvents();
    initContainerEl(containerEl, envData.getDevicePlatform());

    loadLogger(
      envData,
      getAdexpContextSnapshotFn(containerEl, () => {
        return LiftoffMRAID.hasSupport("mraid");
      })
    );

    /** @type {Autoclick[]} */
    const autoclickBus = [];
    LiftoffMRAID.on("clickMethodTried", ({ clickOpenMethod, isAutoclick }) => {
      if (isAutoclick && clickOpenMethod === CLICK_OPEN_METHOD_MRAID) {
        Logger.recordMetricV2(
          "mraid_autoclick_fired_ms",
          Date.now() - LIFTOFF_START_TS
        );
      }
    });

    const env = envData._envData;
    const appStoreData = env.appStoreData;
    // Note(Gibin): Flag to prevent init of AutoEndcard in Tresensa creatives more than once.
    let isTresensaAutoEndcardInit = false;
    let lastChanceEndscreenAutoclick;
    let autoEndcard;

    if (appStoreData) {
      autoEndcard = new LastChanceEndscreen(
        appStoreData,
        containerEl,
        (e) => {
          this.handleClickthrough({
            viewclick,
            containerEl,
            envData,
            allInteractionData: e,
            interceptedDestinationURL: "",
            // Use ae custom parameter to indicate that click was triggered from
            // the auto-endcard.
            customerQueryParams: { ae: true },
            // TODO(oscar): Figure out if this is a Tresensa Creative.
            isTresensaCreative: null,
          });
        },
        env.exchange === "inmobi",
        env
      );
    }

    const viewclick = new Autoclick(envData.getViewclickBehavior(), {
      clickthroughHandler: (e) => {
        this.handleClickthrough({
          viewclick,
          containerEl,
          envData,
          interceptedDestinationURL: "",
          allInteractionData: e,
          customerQueryParams: { mac: 1 },
          // TODO(oscar): Figure out if this is a Tresensa Creative.
          isTresensaCreative: null,
        });
      },
    });
    autoclickBus.push(viewclick);

    useAutoendcardBehaviour = envData.getLastChanceAutoclickBehavior();
    // If exchange is mintegral then only display the auto endcard on delay if
    // platform is ios.
    if (
      useAutoendcardBehaviour &&
      (env.exchange != "mintegral" || env.platform === "ios")
    ) {
      // Autoclick when user doesn't interact and AutoEndcard is shown after 5 seconds
      lastChanceEndscreenAutoclick = new Autoclick(useAutoendcardBehaviour, {
        clickthroughHandler: () => {
          if (autoEndcard) {
            autoEndcard.initAutoEndcard();
          }
        },
      });
      autoclickBus.push(lastChanceEndscreenAutoclick);
    }

    // Autoclick when user interacts and AutoEndcard is shown 7 seconds after the first interaction
    const lastChanceEndscreenInteractionAutoclick = new Autoclick(
      envData.getLastChanceInteractionAutoclickBehavior(),
      {
        clickthroughHandler: () => {
          if (autoEndcard) {
            autoEndcard.initAutoEndcard();
          }
        },
      }
    );
    autoclickBus.push(lastChanceEndscreenInteractionAutoclick);
    Logger.recordMetricV2("initial_load_count", 1);
    LiftoffMRAID.onViewable(() => {
      Logger.recordMetricV2("first_viewable_ms", Date.now() - LIFTOFF_START_TS);
    });
    LiftoffMRAID.onViewable((e) => {
      autoclickBus.forEach((ac) => ac.emitViewableEvent(e));
    }, true);
    LiftoffMRAID.onViewableChange((e) => {
      autoclickBus.forEach((ac) => ac.emitViewableChangeEvent(e));
    });

    const htmlLoadedPromise =
      html != null
        ? Promise.resolve(decodeURIComponent(html))
        : loadExternalHTML(externalHtmlURL);

    return htmlLoadedPromise
      .then((htmlToLoad) => {
        const processedHTML = processHTML({
          envData,
          rawHTML: htmlToLoad,
          rootURL,
          scriptSrcURLs,
          inProd,
        });
        const contentFrame = new ContentFrame({
          envData,
          containerEl,
          processedHTML,
          name: "outer_env",
        });
        contentFrame.onTresensaInteraction(() => {
          // NOTE(Sangeeth): The onTreSensaInteraction handler is only called
          // for tresensa client wrapped playables. These playables run inside
          // an iframe so we can't detect interaction events for them through
          // the interaction monitor.
          vxInteractionListeners.forEach((cb) => cb());
          if (lastChanceEndscreenAutoclick) {
            lastChanceEndscreenAutoclick.disable();
          }
          // TODO(Luis): This is a temporary fix to handle the case where the
          // interaction event is triggered from a TreSensa wrapped 3p.
          // Eventually this needs to be deleted and updated so that a
          // tre_interaction event does trigger a global interaction that can be
          // detected by the AutoClick interaction behaviour.
          if (
            !isTresensaAutoEndcardInit &&
            autoEndcard &&
            lastChanceEndscreenInteractionAutoclick &&
            lastChanceEndscreenInteractionAutoclick._behavior &&
            lastChanceEndscreenInteractionAutoclick._behavior.delay
          ) {
            isTresensaAutoEndcardInit = true;
            setTimeout(() => {
              autoEndcard.initAutoEndcard();
            }, lastChanceEndscreenInteractionAutoclick._behavior.delay);
          }
        });
        contentFrame.onInteractionChange((e) => {
          if (lastChanceEndscreenAutoclick) {
            lastChanceEndscreenAutoclick.disable();
          }
          vxInteractionListeners.forEach((cb) => cb(e));
          autoclickBus.forEach((ac) => ac.emitInteractionEvent(e));
        });
        if (validationCheck) {
          validationResultPromise = new Promise((resolve, reject) => {
            validateContentFrame(contentFrame).then(resolve);
            // #if process.env.NODE_ENV === "test"
//             testResetFunctions.push(reject);
            // #endif
          });

          validationResultPromise.then((result) => {
            validationListeners.forEach((cb) => cb(result));
          });
        }
        // #if process.env.NODE_ENV === "test"
//         testResetFunctions.push(() => {
//           contentFrame.reset();
//           validationResultPromise = null;
//           validationListeners.splice(0, validationListeners.length);
//         });
        // #endif
        configureLogging(envData, contentFrame);
        return contentFrame.getInitCompletePromise();
      })
      .catch((e) => {
        if (!envData.getIsStaffDevice()) {
          const canUnload = window.mraid != null && window.mraid.unload != null;

          if (canUnload) {
            // We know the creative is broken. To prevent bad UX, close the Ad
            // if this is on a non-staff device.
            window.mraid.unload();
          }
        }
        console.error(e);
      })
      .then((contentFrame) => {
        const updateMraidProperties = () => {
          contentFrame.updateMraidProperties(getMraidProperties());
        };

        contentFrame.onMraidCall((methodName, args) => {
          callMraidMethod(
            methodName,
            args,
            contentFrame.notifyOfMraidError.bind(contentFrame)
          );
          updateMraidProperties();
        });

        // NOTE(oscar): The inner env uses synchronous MRAID calls to fetch data
        // that the outer env can only provide asynchronously. Therefore, the
        // inner env has a cache that the outer env needs to update preemptively
        // before any 3rd party code uses out-dated information.
        // Despite it not being a 100% solvable race condition as 3rd party code
        // can always run in the delay between updates, we can mitigate it
        // substantially by hijacking inner env's events so they don't fire
        // until after the updates.
        // E.g. MRAID properties are likely to change during resize, orientation
        // change, and viewable change events.
        // TODO(oscar): Consider hijacking more events.
        window.addEventListener(
          "resize",
          () => {
            updateMraidProperties();
            contentFrame.notifyOfResize();
          },
          { capture: true, passive: true }
        );

        onMraidStateChangeEvent(updateMraidProperties);
        updateMraidProperties();

        // NOTE(Casey): We're currently not relaying error events to the inner
        // env, because these might be caused by calls we make to mraid of which
        // the third-party developer is naive.
        onMraidStateChangeEvent(
          contentFrame.notifyOfMraidEvent.bind(contentFrame)
        );

        contentFrame.onClickthrough(
          (
            interceptedDestinationURL,
            allInteractionData,
            customerQueryParams,
            isTresensaCreative
          ) => {
            this.handleClickthrough({
              viewclick,
              containerEl,
              envData,
              interceptedDestinationURL,
              allInteractionData,
              customerQueryParams,
              isTresensaCreative,
            });
          }
        );

        applyLiftoffPolicy(envData, containerEl, contentFrame);
        contentFrame.notifyOfInitComplete();

        // TODO(Austin): Consider more useful places to log this.
        Logger.recordMetricV2("load_time_ms", Date.now() - LIFTOFF_START_TS);
        autoclickBus.forEach((ac) => ac.emitHtmlLoadedEvent());
      });
  }

  // Called indirectly from interactions and navigation events.
  // interceptedDestinationURL may be null if the click was opened via
  // Liftoff.open.
  static handleClickthrough({
    viewclick,
    containerEl,
    envData,
    interceptedDestinationURL,
    allInteractionData,
    customerQueryParams,
    isTresensaCreative,
    attachAppStoreModal = null,
  }) {
    // NOTE(oscar): We don't want to emit multiple MMP clicks. Product view clicks
    // do not emit MMP clicks.
    if (!customerQueryParams.pv) {
      viewclick.disable();
    }
    if (isCreativeManagedClickthrough(interceptedDestinationURL)) {
      handleCreativeManagedClickthrough(interceptedDestinationURL);
      return;
    }
    if (
      !customerQueryParams.dap &&
      !customerQueryParams.dact &&
      !isTresensaCreative &&
      allInteractionData == null &&
      !customerQueryParams.mac &&
      // NOTE(oscar): It's possible for allInteractionData to be null for ASOD
      // autoclicks.
      !customerQueryParams.pv
    ) {
      // Ignore clickthroughs derived from no user interaction. Exchanges probably
      // block these types of clickthroughs anyways, but we want to avoid pinging
      // tracking urls.
      Logger.recordMetricV2(
        "click_rejection_reason", // should match adexp.go:normalizeMetricV2
        CLICK_REJECTION_REASON_NO_INTERACTIONS
      );
      return;
    }

    if (isCeltraClickthrough(interceptedDestinationURL)) {
      // TODO(Casey): Use ClickHandling.handleClick once the click waterfall is
      // cleaned up.
      handleCreativeManagedClickthrough(interceptedDestinationURL);
      return;
    }

    const clickTrackingParams =
      ClickHandling.getClickTrackingQueryParams(allInteractionData);

    const queryParams = { ...clickTrackingParams, ...customerQueryParams };

    ClickHandling.handleClick({
      clickConfig: envData.getClickConfig(),
      queryParams: queryParams,
      isReengagement: envData.getIsReengagement(),
      platform: envData.getDevicePlatform(),
      exchange: envData.getExchange(),
      abTests: envData.getABTests(),
      // NOTE(Josh): The below parameters are being used temporarily for the app
      // store preview experiment.
      modalContainerEl: containerEl,
      attachAppStoreModal,
    });
  }

  // This function is used by the jsonp callback in the mustache file
  static handleAppStorePreviewJSON(json) {
    if (this.cdnURL != null) {
      this.appStoreModalContext = createAppStoreModalContext(json, this.cdnURL);
    }
  }

  static addValidationListener(cb) {
    if (validationResultPromise != null) {
      validationResultPromise.then(cb);
    } else {
      validationListeners.push(cb);
    }
  }

  /**
   * Enables exclusive format creatives to listen for Madlib CSP interaction events.
   * Note: This method does not register interaction events for non CSP Madlibs.
   */
  static addVXInteractionEventListener(cb) {
    vxInteractionListeners.push(cb);
  }
}

window.LiftoffOuterEnv = LiftoffOuterEnv;

// #if process.env.NODE_ENV === "test"
// export {
//   isCeltraClickthrough,
//   LiftoffOuterEnv,
//   processHTML,
//   htmlStringToDocumentElement,
//   makeScriptEl,
//   makeBaseEl,
//   prependToDocumentHeader,
//   resetForTests,
//   isCreativeManagedClickthrough,
// };
// #endif



// WEBPACK FOOTER //
// ./src/js/madlib/csp-env/outer_env.js